import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminOnly from '../../components/AdminOnly';
import UnknownFaceAvatar from '../../components/UnknownFaceAvatar';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import {
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Pagination,
  Empty,
  Spin,
  message,
  Select,
  Tabs,
  AutoComplete,
  Checkbox,
} from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
  UserAddOutlined,
  SortAscendingOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  EditOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { fetchUnknownList, promoteUnknown } from '../../redux/slices/unknownSlice';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/helpers';

const { Title, Text } = Typography;
const { Option } = Select;

const RowCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
  padding: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f0f0f0;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border-color: #d9d9d9;
  }
`;

const RowContent = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
`;

const UnknownFaceSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 0 0 280px;
  width: 280px;
`;

const UnknownAvatar = styled.div`
  position: relative;
  flex-shrink: 0;

  img {
    width: 100px;
    height: 100px;
    border-radius: 12px;
    object-fit: cover;
    border: 3px solid #e8e8e8;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const UnknownInfo = styled.div`
  flex: 1;

  .unknown-id {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 6px;
  }

  .unknown-time {
    font-size: 13px;
    color: #8c8c8c;
    margin-bottom: 8px;
  }

  .unknown-location {
    margin-bottom: 6px;
  }

  .unknown-first-seen {
    font-size: 12px;
    color: #bfbfbf;
  }
`;

const SuggestionsSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 0 0 auto;
  padding-left: 20px;
`;

const ActionButton = styled(Button)`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8e8e8;
  background: white;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  svg {
    font-size: 18px;
  }

  &.delete-btn {
    &:hover {
      border-color: #ff4d4f;
      color: #ff4d4f;
    }
  }
`;

const SuggestionsContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
`;

const SuggestionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.2;

  &:before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 12px;
    background: ${props => {
      if (props.$status === 'auto') return 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)';
      if (props.$status === 'batch') return 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)';
      return 'linear-gradient(135deg, #d9d9d9 0%, #bfbfbf 100%)';
    }};
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

const SuggestionButton = styled(Button)`
  flex: 0 0 auto;
  width: 240px;
  height: 90px;
  padding: 0;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  border: none;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0;
  overflow: visible;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: transparent !important;

    .suggestion-avatar {
      transform: scale(1.08);
    }
  }

  &:active {
    background: transparent !important;
  }

  .suggestion-content {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0;
    gap: 14px;
  }

  .suggestion-avatar-wrapper {
    position: relative;
    flex-shrink: 0;
    width: 68px;
    height: 68px;
    border-radius: 12px;
    overflow: hidden;

    .initials {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
    }
  }

  .suggestion-avatar {
    width: 68px;
    height: 68px;
    border-radius: 12px;
    object-fit: cover;
    display: block;
    border: 3px solid ${props => {
      if (props.$confidence === 'high') return '#52c41a';
      if (props.$confidence === 'medium') return '#faad14';
      return '#d9d9d9';
    }};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    background: #f0f0f0;
  }

  .confidence-badge {
    position: absolute;
    bottom: -6px;
    right: -6px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => {
      if (props.$confidence === 'high') return 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)';
      if (props.$confidence === 'medium') return 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)';
      return 'linear-gradient(135deg, #d9d9d9 0%, #bfbfbf 100%)';
    }};
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: bold;
    color: white;
  }

  .suggestion-info {
    flex: 1;
    text-align: left;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .suggestion-name {
    font-weight: 700;
    font-size: 15px;
    color: #1a1a1a;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }

  .suggestion-id {
    font-size: 12px;
    color: #8c8c8c;
    font-family: 'SF Mono', 'Monaco', 'Courier New', monospace;
    font-weight: 500;
  }

  .suggestion-score {
    font-size: 12px;
    color: #595959;
    font-weight: 500;
  }

  .match-percentage {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    background: ${props => {
      if (props.$confidence === 'high') return 'rgba(82, 196, 26, 0.15)';
      if (props.$confidence === 'medium') return 'rgba(250, 173, 20, 0.15)';
      return 'rgba(217, 217, 217, 0.15)';
    }};
    color: ${props => {
      if (props.$confidence === 'high') return '#389e0d';
      if (props.$confidence === 'medium') return '#d46b08';
      return '#595959';
    }};
    border-radius: 6px;
    font-weight: 700;
    font-size: 12px;
  }
`;

const ManualEntryButton = styled(Button)`
  flex: 0 0 auto;
  width: 160px;
  border: 2px dashed #d0d0d0;
  background: #fafafa;
  color: #595959;
  height: 90px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;

  &:hover {
    border-color: #1890ff !important;
    color: #1890ff !important;
    background: #e6f7ff !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.12);
  }

  &:active {
    transform: translateY(0);
  }

  .anticon {
    font-size: 16px;
  }

  span {
    line-height: 1;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;

  img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.05);
    }
  }
`;

const UnknownFaces = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading, total } = useSelector((state) => state.unknown);
  const { user, isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [selectedFace, setSelectedFace] = useState(null);
  const [promoteModal, setPromoteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [sortBy, setSortBy] = useState('last_seen_today');
  const [photosList, setPhotosList] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [batchMerging, setBatchMerging] = useState(false);
  const [batchEligibleCount, setBatchEligibleCount] = useState(0);
  const [countingEligible, setCountingEligible] = useState(false);
  const [form] = Form.useForm();

  // Multi-select state
  const [selectedFaces, setSelectedFaces] = useState([]); // {unknown_id, selected_emp_id}
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState({}); // {unknown_id: emp_id}

  // Employee search autocomplete state
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);

  // Use ref to prevent duplicate API calls
  const countingEligibleRef = useRef(false);

  // Debounce timer refs for employee search
  const suggestionDebounceTimer = useRef(null);
  const isSearchingEmployeesRef = useRef(false);
  const lastEmployeeSearchValue = useRef('');

  // Sync form mode with activeTab
  useEffect(() => {
    if (promoteModal) {
      form.setFieldsValue({ mode: activeTab });
    }
  }, [activeTab, promoteModal, form]);

  // Count faces eligible for batch merge (45%+) across ALL pages using dedicated API
  const updateFullCount = useCallback(async () => {
    if (countingEligibleRef.current) return;

    countingEligibleRef.current = true;
    setCountingEligible(true);
    try {
      const response = await api.countBatchMergeEligible(0.45);
      setBatchEligibleCount(response.eligible_count || 0);
    } catch (error) {
      console.error('Failed to count batch eligible faces:', error);
      // Fallback to 0 if API fails
      setBatchEligibleCount(0);
    } finally {
      countingEligibleRef.current = false;
      setCountingEligible(false);
    }
  }, []); // Empty deps - ref prevents duplicate calls, state updates UI

  // Load count on mount only, not on every list change
  useEffect(() => {
    updateFullCount();
  }, [updateFullCount]); // Only run when component mounts

  const loadData = useCallback(() => {
    const params = {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      order_by: sortBy,
      include_suggestions: true,
    };

    // For regular users, filter by their emp_id
    if (!isAdmin() && user?.emp_id) {
      params.filter_emp_id = user.emp_id;
    }

    dispatch(fetchUnknownList(params));
  }, [dispatch, page, pageSize, sortBy, user, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleViewPhotos = async (face) => {
    setSelectedFace(face);
    setViewModal(true);
    try {
      const response = await api.getUnknownPhotos(face.unknown_id);
      setPhotosList(response.photos || []);
    } catch (error) {
      message.error('Failed to load photos');
      setPhotosList([]);
    }
  };

  const handlePromote = async (values) => {
    try {
      const payload = {
        unknown_id: selectedFace.unknown_id,
        emp_id: values.emp_id,
        mode: values.mode || activeTab, // Fallback to activeTab if mode is not set
      };

      console.log('Promote payload:', payload); // Debug log

      if (payload.mode === 'new') {
        payload.full_name = values.full_name;
        payload.email = values.email || null;
      }

      await dispatch(promoteUnknown(payload)).unwrap();

      message.success(`Successfully ${payload.mode === 'new' ? 'created new employee' : 'added to existing employee'}!`);
      setPromoteModal(false);
      form.resetFields();
      setActiveTab('new');
      setEmployeeSuggestions([]);
      loadData();
    } catch (error) {
      console.error('Promote error:', error); // Debug log

      // Show user-friendly error messages
      let errorMessage = error || 'Failed to promote';

      if (typeof error === 'string') {
        // Distinguish between different "not found" errors
        if (error.includes('Unknown ID') && error.includes('not found')) {
          errorMessage = `This unknown face was already merged or deleted. Please refresh the page.`;
        } else if (error.includes('Employee') && error.includes('not found') && activeTab === 'existing') {
          errorMessage = `Employee ID not found. Please check the ID or create a new employee instead.`;
        } else if (error.includes('already exists')) {
          errorMessage = `Employee ID already exists. Use "Add to Existing Employee" tab instead.`;
        }
      }

      message.error(errorMessage);

      // If unknown face was deleted, refresh the list
      if (error.includes('Unknown ID') && error.includes('not found')) {
        setPromoteModal(false);
        loadData(); // Refresh to remove deleted faces
      }
    }
  };

  const showPromoteModal = (face) => {
    setSelectedFace(face);
    setActiveTab('new');
    form.resetFields();
    form.setFieldsValue({ mode: 'new' });
    setPromoteModal(true);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const handleQuickMatch = async (face, emp_id) => {
    // Find the suggestion details for the selected employee
    const suggestion = face.suggestions?.find(s => s.emp_id === emp_id);

    if (!suggestion) {
      message.error('Employee not found in suggestions');
      return;
    }

    Modal.confirm({
      title: 'Confirm Match',
      icon: <CheckOutlined style={{ color: '#52c41a' }} />,
      content: (
        <div>
          <p>Are you sure this person is <strong>{suggestion.full_name}</strong> ({suggestion.emp_id})?</p>
          <p style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
            Match Confidence: {suggestion.confidence.toUpperCase()} ({(suggestion.match_score * 100).toFixed(1)}%)
          </p>
          <p style={{ fontSize: 12, color: '#8c8c8c' }}>
            This will merge all photos and logs into the employee profile.
          </p>
        </div>
      ),
      okText: 'Yes, Confirm Match',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.mergeUnknownToEmployee(face.unknown_id, suggestion.emp_id);
          message.success(`Successfully merged ${face.unknown_id} into ${suggestion.full_name}'s profile!`);
          loadData();
        } catch (error) {
          const errorMsg = error.message || 'Failed to merge';

          // Handle "unknown face not found" error
          if (errorMsg.includes('Unknown') && errorMsg.includes('not found')) {
            message.error('This unknown face was already merged or deleted. Refreshing list...');
            loadData(); // Refresh to remove deleted faces
          } else {
            message.error(errorMsg);
          }
        }
      },
    });
  };

  const handleDelete = (face) => {
    Modal.confirm({
      title: 'Delete Unknown Face',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ${face.unknown_id}? This will remove all photos and logs permanently.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.deleteUnknownFace(face.unknown_id);
          message.success(`Successfully deleted ${face.unknown_id}`);
          loadData();
        } catch (error) {
          const errorMsg = error.message || 'Failed to delete';

          // Handle "unknown face not found" error
          if (errorMsg.includes('Unknown') && errorMsg.includes('not found')) {
            message.info('This unknown face was already deleted. Refreshing list...');
            loadData(); // Refresh to remove deleted faces
          } else {
            message.error(errorMsg);
          }
        }
      },
    });
  };

  const handleDeleteAll = () => {
    Modal.confirm({
      title: 'Delete All Unknown Faces',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ALL ${total} unknown faces? This action cannot be undone and will remove all photos and logs permanently.`,
      okText: 'Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const result = await api.deleteAllUnknownFaces();
          message.success(`Successfully deleted ${result.unknown_faces_deleted} unknown faces`);
          loadData();
        } catch (error) {
          message.error(error.message || 'Failed to delete all');
        }
      },
    });
  };

  const handleSearchEmployees = (searchText) => {
    // Clear previous timer
    if (suggestionDebounceTimer.current) {
      clearTimeout(suggestionDebounceTimer.current);
    }

    if (!searchText || searchText.length < 2) {
      setEmployeeSuggestions([]);
      lastEmployeeSearchValue.current = '';
      return;
    }

    // Skip if already searching
    if (isSearchingEmployeesRef.current) {
      return;
    }

    // Debounce the search to reduce API calls
    suggestionDebounceTimer.current = setTimeout(async () => {
      // Double check before making API call
      if (isSearchingEmployeesRef.current) {
        return;
      }

      try {
        isSearchingEmployeesRef.current = true;
        lastEmployeeSearchValue.current = searchText;
        setSearchingEmployees(true);

        const result = await api.getEmployeeList({
          search: searchText,
          limit: 10,
          offset: 0,
        });

        // Filter results to only include employees that actually match the search term
        const searchLower = searchText.toLowerCase();
        const filteredData = result.data.filter((emp) => {
          const empIdMatch = emp.emp_id?.toLowerCase().includes(searchLower);
          const nameMatch = emp.full_name?.toLowerCase().includes(searchLower);
          const emailMatch = emp.email?.toLowerCase().includes(searchLower);
          return empIdMatch || nameMatch || emailMatch;
        });

        // Limit to top 5 matches
        const topMatches = filteredData.slice(0, 5);

        const suggestions = topMatches.map((emp) => ({
          value: emp.emp_id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <div>
                  <div><Text strong>{emp.emp_id}</Text> - {emp.full_name}</div>
                  {emp.email && <Text type="secondary" style={{ fontSize: 12 }}>{emp.email}</Text>}
                </div>
              </Space>
              {emp.department && <Tag color="blue">{emp.department}</Tag>}
            </div>
          ),
        }));

        setEmployeeSuggestions(suggestions);
      } catch (error) {
        console.error('Error searching employees:', error);
        setEmployeeSuggestions([]);
      } finally {
        setSearchingEmployees(false);
        isSearchingEmployeesRef.current = false;
      }
    }, 400); // 400ms delay to reduce API calls while typing
  };

  const handleToggleSelection = (face) => {
    setSelectedFaces(prev => {
      const exists = prev.find(f => f.unknown_id === face.unknown_id);
      if (exists) {
        // Remove from selection
        const updated = prev.filter(f => f.unknown_id !== face.unknown_id);
        // Also remove selected employee for this face
        setSelectedEmployees(prevEmp => {
          const newEmp = { ...prevEmp };
          delete newEmp[face.unknown_id];
          return newEmp;
        });
        return updated;
      } else {
        // Add to selection with default employee (first suggestion or none)
        const defaultEmpId = face.suggestions?.[0]?.emp_id || null;
        setSelectedEmployees(prevEmp => ({
          ...prevEmp,
          [face.unknown_id]: defaultEmpId
        }));
        return [...prev, { unknown_id: face.unknown_id }];
      }
    });
  };

  const handleEmployeeSelect = (unknown_id, emp_id) => {
    setSelectedEmployees(prev => ({
      ...prev,
      [unknown_id]: emp_id
    }));
  };

  const handleSelectAll = () => {
    if (selectedFaces.length === list.length) {
      setSelectedFaces([]);
      setSelectedEmployees({});
    } else {
      const newSelectedFaces = list.map(face => ({ unknown_id: face.unknown_id }));
      const newSelectedEmployees = {};
      list.forEach(face => {
        if (face.suggestions && face.suggestions.length > 0) {
          newSelectedEmployees[face.unknown_id] = face.suggestions[0].emp_id;
        }
      });
      setSelectedFaces(newSelectedFaces);
      setSelectedEmployees(newSelectedEmployees);
    }
  };

  const handleBatchMergeSelected = () => {
    if (selectedFaces.length === 0) {
      message.warning('Please select at least one unknown face to merge');
      return;
    }

    // Prepare merge requests using user-selected employees
    const mergeRequests = selectedFaces
      .filter(f => selectedEmployees[f.unknown_id]) // Only include faces with selected employee
      .map(f => ({ unknown_id: f.unknown_id, emp_id: selectedEmployees[f.unknown_id] }));

    if (mergeRequests.length === 0) {
      message.warning('Please select an employee for each unknown face');
      return;
    }

    // Check for faces without employee selection
    const facesWithoutSelection = selectedFaces.filter(f => !selectedEmployees[f.unknown_id]);
    if (facesWithoutSelection.length > 0) {
      message.warning(`${facesWithoutSelection.length} face(s) don't have an employee selected`);
      return;
    }

    Modal.confirm({
      title: 'Batch Merge Selected Faces',
      icon: <CheckOutlined style={{ color: '#52c41a' }} />,
      width: 520,
      content: (
        <div>
          <p style={{ fontSize: 14, marginBottom: 12 }}>
            This will merge <strong>{mergeRequests.length} selected unknown faces</strong> to their top suggested employees.
          </p>
          <div style={{
            background: '#f0f2f5',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 12
          }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              📊 <strong>Faces to merge:</strong> {mergeRequests.length}
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#ff9800', marginTop: 12 }}>
            ⚠️ <strong>Warning:</strong> This action cannot be undone. Faces will be permanently merged to employee profiles.
          </p>
        </div>
      ),
      okText: `Yes, Merge ${mergeRequests.length} Faces`,
      cancelText: 'Cancel',
      okButtonProps: { danger: false, type: 'primary', style: { background: '#52c41a', borderColor: '#52c41a' } },
      onOk: async () => {
        try {
          setBatchMerging(true);
          const result = await api.batchMergeSelectedUnknownFaces(mergeRequests);

          // Show results modal
          const mergedResults = result.results?.filter(r => r.status === 'merged') || [];
          const failedResults = result.results?.filter(r => r.status === 'failed') || [];

          Modal.success({
            title: '✅ Batch Merge Completed',
            width: 700,
            content: (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 12,
                  marginBottom: 20
                }}>
                  <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    padding: '12px',
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                      {result.merged_count}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Merged</div>
                  </div>
                  <div style={{
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    padding: '12px',
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff4d4f' }}>
                      {result.failed_count}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Failed</div>
                  </div>
                </div>

                {mergedResults.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: '#52c41a'
                    }}>
                      ✓ Successfully Merged:
                    </div>
                    <div style={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      background: '#fafafa',
                      padding: 12,
                      borderRadius: 6
                    }}>
                      {mergedResults.map((r, idx) => (
                        <div key={idx} style={{
                          fontSize: 12,
                          padding: '4px 0',
                          borderBottom: idx < mergedResults.length - 1 ? '1px solid #e8e8e8' : 'none'
                        }}>
                          <strong>{r.unknown_id}</strong> → {r.full_name} ({r.emp_id})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {failedResults.length > 0 && (
                  <div>
                    <div style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: '#ff4d4f'
                    }}>
                      ✗ Failed to Merge:
                    </div>
                    <div style={{
                      maxHeight: 150,
                      overflowY: 'auto',
                      background: '#fff1f0',
                      padding: 12,
                      borderRadius: 6
                    }}>
                      {failedResults.map((r, idx) => (
                        <div key={idx} style={{
                          fontSize: 12,
                          padding: '4px 0',
                          borderBottom: idx < failedResults.length - 1 ? '1px solid #ffa39e' : 'none'
                        }}>
                          <strong>{r.unknown_id}</strong> → {r.emp_id}
                          <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                            Error: {r.error}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          });

          setSelectedFaces([]);
          setSelectedEmployees({});
          setSelectionMode(false);
          loadData();
        } catch (error) {
          message.error(error.message || 'Batch merge failed');
        } finally {
          setBatchMerging(false);
        }
      },
    });
  };

  const handleBatchMerge = () => {
    Modal.confirm({
      title: 'Batch Merge Unknown Faces',
      icon: <CheckOutlined style={{ color: '#52c41a' }} />,
      width: 520,
      content: (
        <div>
          <p style={{ fontSize: 14, marginBottom: 12 }}>
            This will automatically merge all unknown faces with <strong>≥45% match confidence</strong> to their suggested employees.
          </p>
          <div style={{
            background: '#f0f2f5',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 12
          }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              📊 <strong>Faces to merge:</strong> {batchEligibleCount}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              • Includes faces with 45-50% (batch-merge ready)<br/>
              • Includes faces with ≥50% (not yet auto-merged)
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#ff9800', marginTop: 12 }}>
            ⚠️ <strong>Warning:</strong> This action cannot be undone. Faces will be permanently merged to employee profiles.
          </p>
        </div>
      ),
      okText: `Yes, Merge ${batchEligibleCount} Faces`,
      cancelText: 'Cancel',
      okButtonProps: { danger: false, type: 'primary', style: { background: '#52c41a', borderColor: '#52c41a' } },
      onOk: async () => {
        try {
          setBatchMerging(true);
          const result = await api.batchMergeUnknownFaces(0.45);

          // Show results modal with complete information
          const mergedResults = result.results?.filter(r => r.status === 'merged') || [];
          const failedResults = result.results?.filter(r => r.status === 'failed') || [];

          Modal.success({
            title: '✅ Batch Merge Completed',
            width: 700,
            content: (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12,
                  marginBottom: 20
                }}>
                  <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    padding: '12px',
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                      {result.merged_count}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Merged</div>
                  </div>
                  <div style={{
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    padding: '12px',
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#faad14' }}>
                      {result.skipped_count}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Skipped</div>
                  </div>
                  <div style={{
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    padding: '12px',
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff4d4f' }}>
                      {result.failed_count}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Failed</div>
                  </div>
                </div>

                {mergedResults.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: '#52c41a'
                    }}>
                      ✓ Successfully Merged:
                    </div>
                    <div style={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      background: '#fafafa',
                      padding: 12,
                      borderRadius: 6
                    }}>
                      {mergedResults.map((r, idx) => (
                        <div key={idx} style={{
                          fontSize: 12,
                          padding: '4px 0',
                          borderBottom: idx < mergedResults.length - 1 ? '1px solid #e8e8e8' : 'none'
                        }}>
                          <strong>{r.unknown_id}</strong> → {r.full_name} ({r.emp_id})
                          <span style={{ color: '#52c41a', marginLeft: 8 }}>
                            {r.match_score}% match
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {failedResults.length > 0 && (
                  <div>
                    <div style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: '#ff4d4f'
                    }}>
                      ✗ Failed to Merge:
                    </div>
                    <div style={{
                      maxHeight: 150,
                      overflowY: 'auto',
                      background: '#fff1f0',
                      padding: 12,
                      borderRadius: 6
                    }}>
                      {failedResults.map((r, idx) => (
                        <div key={idx} style={{
                          fontSize: 12,
                          padding: '4px 0',
                          borderBottom: idx < failedResults.length - 1 ? '1px solid #ffa39e' : 'none'
                        }}>
                          <strong>{r.unknown_id}</strong> → {r.full_name} ({r.emp_id})
                          <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                            Error: {r.error}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          });

          loadData();
        } catch (error) {
          message.error(error.message || 'Batch merge failed');
        } finally {
          setBatchMerging(false);
        }
      },
    });
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div></div>
        <div style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center'
        }}>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            style={{
              width: 180,
              height: 44
            }}
            size="large"
            prefix={<SortAscendingOutlined />}
          >
            <Option value="last_seen_today">Latest Seen</Option>
            <Option value="first_seen_today">First Seen</Option>
            <Option value="date_added">Date Added</Option>
            <Option value="unknown_id">ID (A-Z)</Option>
          </Select>

          {/* Multi-Select Mode Toggle */}
          <AdminOnly>
            <Button
              type={selectionMode ? 'primary' : 'default'}
              icon={<CheckSquareOutlined />}
              size="large"
              onClick={() => {
                setSelectionMode(!selectionMode);
                setSelectedFaces([]);
                setSelectedEmployees({});
              }}
              style={{
                minWidth: 140,
                height: 44,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 500
              }}
            >
              {selectionMode ? 'Cancel Select' : 'Multi-Select'}
            </Button>
          </AdminOnly>

          {/* Show merge selected button when in selection mode */}
          {selectionMode && selectedFaces.length > 0 && (
            <>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                size="large"
                onClick={handleBatchMergeSelected}
                loading={batchMerging}
                style={{
                  background: '#1890ff',
                  borderColor: '#1890ff',
                  minWidth: 200,
                  height: 44,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500
                }}
              >
                Merge Selected ({selectedFaces.length})
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="large"
                onClick={() => {
                  Modal.confirm({
                    title: 'Delete Selected Unknown Faces',
                    icon: <ExclamationCircleOutlined />,
                    content: `Are you sure you want to delete ${selectedFaces.length} selected unknown face${selectedFaces.length > 1 ? 's' : ''}? This action cannot be undone and will remove all photos and logs permanently.`,
                    okText: 'Delete',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk: async () => {
                      try {
                        const deletePromises = selectedFaces.map(face =>
                          api.deleteUnknownFace(face.unknown_id)
                        );
                        await Promise.all(deletePromises);
                        message.success(`Successfully deleted ${selectedFaces.length} unknown face${selectedFaces.length > 1 ? 's' : ''}`);
                        setSelectedFaces([]);
                        setSelectedEmployees({});
                        setSelectionMode(false);
                        loadData();
                      } catch (error) {
                        message.error(error.message || 'Failed to delete selected faces');
                      }
                    },
                  });
                }}
                style={{
                  minWidth: 200,
                  height: 44,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500
                }}
              >
                Delete Selected ({selectedFaces.length})
              </Button>
            </>
          )}

          {/* Merge All 45%+ button (only show when not in selection mode) */}
          {!selectionMode && batchEligibleCount > 0 ? (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="large"
              onClick={handleBatchMerge}
              onMouseEnter={updateFullCount}
              loading={batchMerging || countingEligible}
              style={{
                background: '#52c41a',
                borderColor: '#52c41a',
                minWidth: 200,
                height: 44,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 500
              }}
              title={countingEligible ? 'Counting eligible faces...' : `Merge ${batchEligibleCount} faces with ≥45% match confidence`}
            >
              Merge All 45%+ ({countingEligible ? '...' : batchEligibleCount})
            </Button>
          ) : !selectionMode ? (
            <Button
              type="default"
              icon={<CheckOutlined />}
              size="large"
              disabled
              onMouseEnter={updateFullCount}
              loading={countingEligible}
              style={{
                minWidth: 200,
                height: 44,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 500
              }}
              title={countingEligible ? 'Counting eligible faces...' : 'No faces with ≥45% match confidence'}
            >
              Merge All 45%+ ({countingEligible ? '...' : batchEligibleCount})
            </Button>
          ) : null}

          <AdminOnly>
            <Button
              danger
              icon={<DeleteOutlined />}
              size="large"
              onClick={handleDeleteAll}
              disabled={total === 0 || selectionMode}
              style={{
                minWidth: 140,
                height: 44,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 500
              }}
            >
              Delete All
            </Button>
          </AdminOnly>
          <Tag
            color="orange"
            style={{
              fontSize: 15,
              padding: '0 16px',
              height: 44,
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600,
              border: '2px solid #ffa940',
              borderRadius: 8,
              lineHeight: '44px',
              margin: 0
            }}
          >
            {total} Unknown Faces
          </Tag>
        </div>
      </div>

      {/* Selection Mode Header */}
      {selectionMode && list && list.length > 0 && (
        <div style={{
          background: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: 8,
          padding: '12px 20px',
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Checkbox
              checked={selectedFaces.length === list.length}
              indeterminate={selectedFaces.length > 0 && selectedFaces.length < list.length}
              onChange={handleSelectAll}
            />
            <Text strong>
              {selectedFaces.length === 0
                ? 'Select faces to merge'
                : `${selectedFaces.length} face${selectedFaces.length > 1 ? 's' : ''} selected`}
            </Text>
          </div>
          {selectedFaces.length > 0 && (
            <Button
              type="link"
              onClick={() => setSelectedFaces([])}
              style={{ padding: 0 }}
            >
              Clear Selection
            </Button>
          )}
        </div>
      )}

      <Spin spinning={loading}>
        {(() => {
          // Backend now handles filtering for regular users
          return list && list.length > 0 ? (
          <>
            <div>
              {list.map((face, index) => (
                <motion.div
                  key={face.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <RowCard>
                    <RowContent>
                      {/* Checkbox for multi-select */}
                      {selectionMode && (
                        <div style={{ paddingRight: 16 }}>
                          <Checkbox
                            checked={selectedFaces.some(f => f.unknown_id === face.unknown_id)}
                            onChange={() => handleToggleSelection(face)}
                            style={{ transform: 'scale(1.3)' }}
                          />
                        </div>
                      )}

                      {/* Unknown Face Section */}
                      <UnknownFaceSection>
                        <UnknownAvatar>
                          <UnknownFaceAvatar
                            unknownId={face.unknown_id}
                            photoIndex={0}
                            size={100}
                            enablePreview={true}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </UnknownAvatar>
                        <UnknownInfo>
                          <div className="unknown-id" style={{
                            fontSize: '19px',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            color: '#1890ff',
                            letterSpacing: '0.5px'
                          }}>
                            {face.unknown_id}
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            color: '#52c41a',
                            marginTop: '4px',
                            fontWeight: 500
                          }}>
                            <ClockCircleOutlined style={{ fontSize: '12px' }} />
                            <span>Last: {formatDateTime(face.last_seen_today)}</span>
                          </div>
                          <div className="unknown-location" style={{ marginTop: '6px' }}>
                            <Tag color="blue" icon={<EnvironmentOutlined />}>{face.last_seen_camera_zone}</Tag>
                          </div>
                          <div className="unknown-first-seen" style={{
                            fontSize: '12px',
                            color: '#8c8c8c',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginTop: '4px'
                          }}>
                            <ClockCircleOutlined style={{ fontSize: '11px' }} />
                            First seen: {formatDateTime(face.first_seen_today)}
                          </div>
                        </UnknownInfo>
                      </UnknownFaceSection>

                      {/* Suggestions Section - Always show */}
                      <SuggestionsSection>
                        <SuggestionTitle
                          $status={
                            face.suggestions && face.suggestions.length > 0 && face.suggestions[0].match_score >= 0.50 ? 'auto'
                            : face.suggestions && face.suggestions.length > 0 && face.suggestions[0].match_score >= 0.45 ? 'batch'
                            : 'manual'
                          }
                        >
                          {face.suggestions && face.suggestions.length > 0
                            ? `Match Suggestions (${face.suggestions.length})`
                            : 'No Match Suggestions'}
                        </SuggestionTitle>
                        <SuggestionsContainer>
                          {face.suggestions && face.suggestions.length > 0 ? (
                            <>
                              {/* Show all suggestions as cards */}
                              {face.suggestions.map((suggestion, idx) => (
                                <SuggestionButton
                                  key={idx}
                                  $confidence={
                                    suggestion.match_score >= 0.50 ? 'high'
                                    : suggestion.match_score >= 0.45 ? 'medium'
                                    : 'low'
                                  }
                                  onClick={() => {
                                    if (selectionMode) {
                                      // In selection mode, just update the selected employee
                                      handleEmployeeSelect(face.unknown_id, suggestion.emp_id);
                                    } else {
                                      // In normal mode, merge immediately
                                      handleQuickMatch(face, suggestion.emp_id);
                                    }
                                  }}
                                  style={{
                                    opacity: selectionMode && !selectedFaces.some(f => f.unknown_id === face.unknown_id) ? 0.5 : 1,
                                    cursor: selectionMode && !selectedFaces.some(f => f.unknown_id === face.unknown_id) ? 'not-allowed' : 'pointer',
                                    border: selectionMode && selectedEmployees[face.unknown_id] === suggestion.emp_id ? '3px solid #1890ff' : undefined
                                  }}
                                  disabled={selectionMode && !selectedFaces.some(f => f.unknown_id === face.unknown_id)}
                                >
                                  <div className="suggestion-content">
                                    <div
                                      className="suggestion-avatar-wrapper"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <EmployeeAvatar
                                        empId={suggestion.emp_id}
                                        size={56}
                                        enablePreview={true}
                                        photoUpdatedAt={suggestion.photo_updated_at}
                                      />
                                      <div className="confidence-badge">
                                        {suggestion.confidence === 'high' ? '✓' : suggestion.confidence === 'medium' ? '~' : '?'}
                                      </div>
                                    </div>
                                    <div className="suggestion-info">
                                      <div className="suggestion-name">{suggestion.full_name}</div>
                                      <div className="suggestion-id">{suggestion.emp_id}</div>
                                      <div className="suggestion-score">
                                        <span className="match-percentage">
                                          {(suggestion.match_score * 100).toFixed(0)}% match
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </SuggestionButton>
                              ))}
                            </>
                          ) : (
                            <div style={{
                              flex: 1,
                              padding: '32px',
                              textAlign: 'center',
                              color: '#8c8c8c',
                              background: '#fafafa',
                              borderRadius: '12px',
                              border: '2px dashed #d9d9d9'
                            }}>
                              <SearchOutlined style={{ fontSize: '32px', marginBottom: '8px', color: '#bfbfbf' }} />
                              <div style={{ fontSize: '14px', fontWeight: 500 }}>
                                No matching employees found in database
                              </div>
                              <div style={{ fontSize: '12px', marginTop: '4px', color: '#bfbfbf' }}>
                                Use the button below to create a new employee
                              </div>
                            </div>
                          )}

                          {/* Action buttons */}
                          {isAdmin() && (
                            <ManualEntryButton
                              onClick={() => showPromoteModal(face)}
                            >
                              <EditOutlined />
                              <span>{face.suggestions && face.suggestions.length > 0 ? 'Neither' : 'Create New'}</span>
                            </ManualEntryButton>
                          )}
                          {!isAdmin() && face.suggestions && face.suggestions.length > 0 && (
                            <ManualEntryButton
                              onClick={() => handleDelete(face)}
                              style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                            >
                              <DeleteOutlined />
                              <span>Not Me</span>
                            </ManualEntryButton>
                          )}
                        </SuggestionsContainer>
                      </SuggestionsSection>

                      {/* Action Buttons */}
                      <ActionButtons>
                        <ActionButton
                          icon={<EyeOutlined />}
                          onClick={() => handleViewPhotos(face)}
                          title="View Photos"
                        />
                        <ActionButton
                          icon={<SearchOutlined />}
                          onClick={() => navigate(`/unknown-faces/match/${face.unknown_id}`)}
                          title="Find Matches"
                        />
                        {isAdmin() && (
                          <>
                            <ActionButton
                              icon={<UserAddOutlined />}
                              onClick={() => showPromoteModal(face)}
                              title="Promote"
                            />
                            <ActionButton
                              className="delete-btn"
                              icon={<DeleteOutlined />}
                              onClick={() => handleDelete(face)}
                              title="Delete"
                            />
                          </>
                        )}
                      </ActionButtons>
                    </RowContent>
                  </RowCard>
                </motion.div>
              ))}
            </div>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={(page, pageSize) => {
                  setPage(page);
                  setPageSize(pageSize);
                }}
                showSizeChanger
                showTotal={(total) => `Total ${total} unknown faces`}
                pageSizeOptions={['12', '24', '48', '96']}
              />
            </div>
          </>
        ) : (
          <Empty
            description={
              !isAdmin() && list && list.length > 0
                ? "No unknown faces with match suggestions found"
                : "No unknown faces found"
            }
          />
        );
        })()}
      </Spin>

      {/* View Photos Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px'
            }}>
              Unknown Person Details
            </span>
            <Tag
              color="blue"
              style={{
                fontSize: '14px',
                padding: '4px 12px',
                fontFamily: 'monospace',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}
            >
              {selectedFace?.unknown_id}
            </Tag>
          </div>
        }
        open={viewModal}
        onCancel={() => {
          setViewModal(false);
          setPhotosList([]);
        }}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)}>
            Close
          </Button>,
          <Button
            key="promote"
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              setViewModal(false);
              showPromoteModal(selectedFace);
            }}
          >
            Promote to Employee
          </Button>,
        ]}
        width={800}
      >
        {selectedFace && (
          <div>
            <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Text strong style={{ minWidth: '120px', fontSize: '14px' }}>Unknown ID:</Text>
                <Text style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 600 }}>
                  {selectedFace.unknown_id}
                </Text>
              </div>

              <div style={{
                background: '#f0f7ff',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #d6e4ff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                  <Text strong style={{ fontSize: '14px' }}>First Seen:</Text>
                  <Text style={{ fontSize: '14px', color: '#595959' }}>
                    {formatDateTime(selectedFace.first_seen_today)}
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ClockCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                  <Text strong style={{ fontSize: '14px' }}>Last Seen:</Text>
                  <Text style={{ fontSize: '14px', color: '#595959' }}>
                    {formatDateTime(selectedFace.last_seen_today)}
                  </Text>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <Text strong style={{ minWidth: '120px', fontSize: '14px' }}>Camera Location:</Text>
                <Tag color="blue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                  {selectedFace.last_seen_camera_zone}
                </Tag>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Text strong style={{ minWidth: '120px', fontSize: '14px' }}>Total Photos:</Text>
                <Text style={{ fontSize: '14px', fontWeight: 600, color: '#1890ff' }}>
                  {photosList.length}
                </Text>
              </div>
            </Space>

            {photosList.length > 0 ? (
              <ImageGallery>
                {photosList.map((photo, index) => (
                  <div key={index} style={{ display: 'inline-block' }}>
                    <UnknownFaceAvatar
                      unknownId={selectedFace.unknown_id}
                      photoIndex={index}
                      size={120}
                      enablePreview={true}
                    />
                  </div>
                ))}
              </ImageGallery>
            ) : (
              <Empty description="No photos available" />
            )}
          </div>
        )}
      </Modal>

      {/* Promote Modal */}
      <Modal
        title={`Promote ${selectedFace?.unknown_id} to Employee`}
        open={promoteModal}
        onCancel={() => {
          setPromoteModal(false);
          form.resetFields();
          setActiveTab('new');
        }}
        footer={null}
        width={600}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            const currentEmpId = form.getFieldValue('emp_id');
            form.resetFields();
            form.setFieldsValue({ mode: key, emp_id: currentEmpId });
          }}
          style={{ marginTop: 16 }}
          items={[
            { key: 'new', label: 'Create New Employee' },
            { key: 'existing', label: 'Add to Existing Employee' },
          ]}
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePromote}
          style={{ marginTop: 16 }}
        >
          <Form.Item name="mode" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            key={`emp_id_${activeTab}`}
            name="emp_id"
            label="Employee ID"
            rules={[{ required: true, message: 'Please enter employee ID' }]}
            tooltip={activeTab === 'existing' ? 'Type to search for existing employees' : 'Create a new unique employee ID'}
          >
            <AutoComplete
              options={activeTab === 'existing' ? employeeSuggestions : []}
              onSearch={activeTab === 'existing' ? handleSearchEmployees : undefined}
              onSelect={(value) => {
                // When user selects from dropdown, update form field
                form.setFieldsValue({ emp_id: value });
              }}
              onChange={(value) => {
                // When user types, also update form field
                form.setFieldsValue({ emp_id: value });
              }}
              placeholder={activeTab === 'existing' ? 'Search employee by ID or name...' : 'e.g., AVA0001'}
              notFoundContent={searchingEmployees ? <Spin size="small" /> : null}
              popupMatchSelectWidth={400}
              allowClear
              size="large"
              style={{
                width: '100%',
                borderRadius: '8px'
              }}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              {activeTab === 'existing'
                ? '💡 Search and select an existing employee by ID or name'
                : '💡 Enter a unique ID for the new employee (e.g., AVA0001)'}
            </Text>
          </Form.Item>

          {activeTab === 'new' && (
            <>
              <Form.Item
                name="full_name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input placeholder="e.g., John Doe" size="large" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: 'email', message: 'Please enter valid email' }]}
              >
                <Input placeholder="e.g., john.doe@company.com" size="large" />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setPromoteModal(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CheckOutlined />}
                loading={loading}
              >
                {activeTab === 'new' ? 'Create Employee' : 'Add to Employee'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UnknownFaces;
